import * as THREE from 'three';
import { appendFaceBoundary, featureEdges } from './edges';
import { isModelMesh } from './picking';
import { UnionFind, VertexWelder, edgeKey } from './weld';

// An edge that turns by more than this at a vertex ends there, at a corner.
// Tessellated curves turn by far less from one segment to the next.
const CORNER_TURN_DEGREES = 40;
const SMOOTH_COS = -Math.cos(THREE.MathUtils.degToRad(CORNER_TURN_DEGREES));
const NO_NEIGHBOUR = -1;

export interface EdgeSnap {
  point: THREE.Vector3;
  /** The segment the point lies on; null when it is a corner. */
  segment: number | null;
}

function pointId(welder: VertexWelder, positions: Float32Array, offset: number): number {
  return welder.id(positions[offset], positions[offset + 1], positions[offset + 2]);
}

function segmentKey(welder: VertexWelder, positions: Float32Array, segment: number): number {
  return edgeKey(pointId(welder, positions, segment * 6), pointId(welder, positions, segment * 6 + 3));
}

/** A mesh's edges in world space, chained from corner to corner as a CAD user sees them. */
export class MeshEdges {
  readonly corners: readonly THREE.Vector3[];
  private readonly chainOf: Uint32Array;
  private readonly chains = new Map<number, Float32Array>();
  private readonly a = new THREE.Vector3();
  private readonly b = new THREE.Vector3();
  private readonly onRay = new THREE.Vector3();
  private readonly onSegment = new THREE.Vector3();

  /**
   * `positions` holds two xyz endpoints per segment. Where `neighbours` names
   * the face across each segment, an edge also ends where that face changes,
   * which is where a B-rep edge meets a tangent one.
   */
  constructor(
    private readonly positions: Float32Array,
    private readonly neighbours: ArrayLike<number> | null = null,
  ) {
    const count = Math.floor(positions.length / 6);
    const welder = new VertexWelder();
    const ends: number[][] = [];
    for (let end = 0; end < count * 2; end++) {
      const id = pointId(welder, positions, end * 3);
      if (id === ends.length) ends.push([]);
      ends[id].push(end);
    }

    const chains = new UnionFind(count);
    const corners: THREE.Vector3[] = [];
    for (const uses of ends) {
      if (uses.length === 2 && this.isSmooth(uses[0], uses[1])) {
        chains.union(uses[1] >> 1, uses[0] >> 1);
      } else {
        corners.push(new THREE.Vector3().fromArray(positions, uses[0] * 3));
      }
    }
    this.corners = corners;
    this.chainOf = new Uint32Array(count).map((_, segment) => chains.find(segment));
  }

  /** The feature edges of a mesh that carries no B-rep faces. */
  static of(mesh: THREE.Mesh): MeshEdges {
    mesh.updateWorldMatrix(true, false);
    const edges = featureEdges(mesh);
    const position = edges.getAttribute('position');
    const out = new Float32Array(position.count * 3);
    const point = new THREE.Vector3();
    for (let i = 0; i < position.count; i++) {
      point.fromBufferAttribute(position, i).applyMatrix4(mesh.matrixWorld).toArray(out, i * 3);
    }
    edges.dispose();
    return new MeshEdges(out);
  }

  get segmentCount(): number {
    return this.chainOf.length;
  }

  /**
   * The corner, or failing one the point on an edge, nearest `ray` and within
   * `reach` of it that `accept` lets through.
   */
  nearest(ray: THREE.Ray, reach: number, accept: (point: THREE.Vector3) => boolean): EdgeSnap | null {
    const corner = this.nearestCorner(ray, reach, accept);
    return corner ? { point: corner.clone(), segment: null } : this.nearestOnEdge(ray, reach, accept);
  }

  nearestCorner(
    ray: THREE.Ray,
    reach: number,
    accept: (point: THREE.Vector3) => boolean,
  ): THREE.Vector3 | null {
    let best: THREE.Vector3 | null = null;
    let bestDistance = reach * reach;
    for (const corner of this.corners) {
      const distance = ray.distanceSqToPoint(corner);
      if (distance > bestDistance || !accept(corner)) continue;
      bestDistance = distance;
      best = corner;
    }
    return best;
  }

  nearestOnEdge(
    ray: THREE.Ray,
    reach: number,
    accept: (point: THREE.Vector3) => boolean,
  ): (EdgeSnap & { segment: number }) | null {
    let best: (EdgeSnap & { segment: number }) | null = null;
    let bestDistance = reach * reach;
    for (let segment = 0; segment < this.segmentCount; segment++) {
      this.a.fromArray(this.positions, segment * 6);
      this.b.fromArray(this.positions, segment * 6 + 3);
      const distance = ray.distanceSqToSegment(this.a, this.b, this.onRay, this.onSegment);
      if (distance > bestDistance || !accept(this.onSegment)) continue;
      bestDistance = distance;
      best = { point: this.onSegment.clone(), segment };
    }
    return best;
  }

  /** Every segment of the edge that `segment` is part of. */
  chain(segment: number): Float32Array {
    const root = this.chainOf[segment];
    let chain = this.chains.get(root);
    if (!chain) {
      const out: number[] = [];
      this.chainOf.forEach((owner, member) => {
        if (owner === root) out.push(...this.positions.subarray(member * 6, member * 6 + 6));
      });
      chain = new Float32Array(out);
      this.chains.set(root, chain);
    }
    return chain;
  }

  // Ends are numbered two per segment, so `end >> 1` is the segment and
  // `end ^ 1` its other end.
  private isSmooth(first: number, second: number): boolean {
    if (this.neighbours && this.neighbours[first >> 1] !== this.neighbours[second >> 1]) return false;
    const vertex = this.a.fromArray(this.positions, first * 3);
    const out1 = new THREE.Vector3().fromArray(this.positions, (first ^ 1) * 3).sub(vertex);
    const out2 = this.b.fromArray(this.positions, (second ^ 1) * 3).sub(vertex);
    const lengths = out1.length() * out2.length();
    return lengths > 0 && out1.dot(out2) / lengths < SMOOTH_COS;
  }
}

/**
 * The edges of every B-rep face under `model`. OCCT meshes each B-rep edge
 * once for both faces it bounds, so a segment's twin on another face names
 * the face across it.
 */
export function brepFaceEdges(model: THREE.Object3D): Map<THREE.Mesh, MeshEdges> {
  model.updateWorldMatrix(true, true);
  const faces: THREE.Mesh[] = [];
  model.traverse((child) => {
    if (isModelMesh(child)) faces.push(child);
  });
  const boundaries = faces.map((face) => {
    const out: number[] = [];
    appendFaceBoundary(face, face.matrixWorld, out);
    return new Float32Array(out);
  });
  const welder = new VertexWelder();
  const keys = boundaries.map((positions) =>
    Array.from({ length: positions.length / 6 }, (_, segment) => segmentKey(welder, positions, segment)),
  );
  const owners = new Map<number, number[]>();
  keys.forEach((faceKeys, face) => {
    for (const key of faceKeys) {
      const list = owners.get(key);
      if (list) list.push(face);
      else owners.set(key, [face]);
    }
  });
  return new Map(
    faces.map((mesh, face) => {
      const positions = boundaries[face];
      const neighbours = Int32Array.from(
        keys[face],
        (key) => owners.get(key)?.find((owner) => owner !== face) ?? NO_NEIGHBOUR,
      );
      return [mesh, new MeshEdges(positions, neighbours)];
    }),
  );
}

/** Each mesh's snap edges: its B-rep face's boundary on an OCCT model, else its feature edges. */
export function snapEdges(model: THREE.Object3D): (mesh: THREE.Mesh) => MeshEdges {
  let brep: Map<THREE.Mesh, MeshEdges> | null = null;
  return (mesh) => {
    if (model.userData.brepFaces === true) brep ??= brepFaceEdges(model);
    return brep?.get(mesh) ?? MeshEdges.of(mesh);
  };
}

// A point this close on screen to a corner or an edge of the face under it
// lands on that corner or edge.
const SNAP_PX = 10;

/** How far round a hit a snap reaches, and which points it may land on. */
export class SnapScope {
  reach = 0;
  private readonly front = new THREE.Plane();
  private readonly at = new THREE.Vector3();
  private facing = false;
  private clip: THREE.Plane | null = null;

  // A point behind the face under the pointer is hidden by it, so the far
  // side of a curved face never snaps.
  readonly accept = (point: THREE.Vector3): boolean =>
    (!this.facing || this.front.distanceToPoint(point) >= -this.reach) &&
    (!this.clip || this.clip.distanceToPoint(point) >= 0);

  around(
    hit: THREE.Intersection,
    ray: THREE.Ray,
    camera: THREE.Camera,
    heightPx: number,
    clip: THREE.Plane | null,
  ): this {
    const ndc = this.at.copy(hit.point).project(camera);
    ndc.y += (2 * SNAP_PX) / Math.max(heightPx, 1);
    this.reach = ndc.unproject(camera).distanceTo(hit.point);
    this.facing = Boolean(hit.face);
    if (hit.face) {
      const normal = this.at.copy(hit.face.normal).transformDirection(hit.object.matrixWorld);
      if (normal.dot(ray.direction) > 0) normal.negate();
      this.front.setFromNormalAndCoplanarPoint(normal, hit.point);
    }
    this.clip = clip;
    return this;
  }
}
